/**
 * IterationHistory - Enhanced iteration tracking showing user vs AI feedback
 * Displays feedback effectiveness and improvement trends
 */

import { 
  RefreshCw, 
  RotateCcw, 
  CheckCircle, 
  User, 
  Bot, 
  TrendingUp, 
  TrendingDown,
  Target,
  Brain,
  MessageSquare,
  X
} from 'lucide-react';
import React, { useState } from 'react';
// import { FixedSizeList as List } from 'react-window'; // Optional dependency for virtualization

import { StageType } from '@/types/execution';
import { cn } from '@/utils/cn';

import { QualityScores } from './FeedbackPanel';

export interface EnhancedIteration {
  iteration_number: number;
  trigger: 'ai_evaluation' | 'user_feedback';
  decision: 'accept' | 'improve' | 'rewrite';
  stage?: StageType;
  feedback_source: {
    type: 'gpt5' | 'user' | 'combined';
    user_input?: string;
    ai_evaluation?: {
      feedback: string;
      decision: string;
      confidence: number;
    };
  };
  scores: QualityScores;
  improvements: string[];
  preserved_from_previous: string[];
  persona_used: string;
  timestamp: string;
  is_best_attempt: boolean;
  processing_time_seconds?: number;
  cost_usd?: number;
  assets?: { filename: string; type: 'text' | 'json' | 'image' | 'video' | 'audio' }[];
}

interface IterationHistoryProps {
  stage: StageType;
  iterations: EnhancedIteration[];
  currentIteration?: number;
  onIterationSelect?: (iteration: number) => void;
  onClose?: () => void;
  qualityThreshold?: number;
  className?: string;
  executionId?: string;
}

const IterationHistory: React.FC<IterationHistoryProps> = ({
  stage,
  iterations,
  currentIteration,
  onIterationSelect,
  onClose,
  qualityThreshold = 85,
  className,
  executionId
}) => {
  // Calculate effectiveness metrics
  const calculateEffectiveness = (iteration: EnhancedIteration, previousIteration?: EnhancedIteration) => {
    if (!previousIteration) return { improvement: 0, trend: 'baseline' as const };
    
    const currentScore = (iteration.scores.overall || 0) * 100;
    const previousScore = (previousIteration.scores.overall || 0) * 100;
    const scoreDelta = currentScore - previousScore;
    const trend = scoreDelta > 5 ? 'improving' : 
                 scoreDelta < -5 ? 'declining' : 'stable';
    
    return { improvement: scoreDelta, trend };
  };

  const getPersonaIcon = (personaName: string) => {
    switch (personaName?.toLowerCase()) {
      case 'the writer':
      case 'writer':
        return 'W';
      case 'the architect':
      case 'architect':
        return 'A';
      case 'the operator':
      case 'operator':
        return 'O';
      default:
        return 'AI';
    }
  };

  const getDecisionIcon = (decision: string) => {
    switch (decision) {
      case 'improve':
        return <RefreshCw className="w-4 h-4" />;
      case 'rewrite':
        return <RotateCcw className="w-4 h-4" />;
      case 'accept':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Target className="w-4 h-4" />;
    }
  };


  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const hasFeedback = (it: EnhancedIteration) => {
    return Boolean(it.feedback_source?.user_input || it.feedback_source?.ai_evaluation?.feedback);
  };

  const [preview, setPreview] = useState<{ url: string; type: 'image' | 'video' | 'text'; title: string } | null>(null);
  const [expandedIterations, setExpandedIterations] = useState<Set<number>>(new Set());
  const openPreview = (it: EnhancedIteration) => {
    if (!executionId || !it.assets || it.assets.length === 0) return;
    const asset = it.assets[0];
    const type = asset.type === 'image' ? 'image' : asset.type === 'video' ? 'video' : 'text';
    const url = `/api/pipeline-assets/${executionId}/${it.stage || stage}/${asset.filename}`;
    setPreview({ url, type, title: `${(it.stage || stage)} • Iteration ${it.iteration_number}` });
  };

  if (iterations.length === 0) {
    return (
      <div className={cn('bg-gray-900/90 backdrop-blur-sm rounded-lg p-6 text-center relative border border-white/10', className)}>
        {/* Close button - always visible */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-white/70" />
          </button>
        )}
        <Brain className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
        <h3 className="text-sm font-medium text-white mb-1">No Iterations Yet</h3>
        <p className="text-sm text-white/60">
          Iterations will appear here as the {stage} stage processes
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4 relative', className)}>
      {/* Close button - always visible for modal usage */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-0 right-0 z-10 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-white/70" />
        </button>
      )}
      
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Iteration History</h3>
        <div className="text-sm text-white/60 mr-10">
          {iterations.length} iteration{iterations.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Note: Virtualization disabled pending react-window dependency */}
      {false && iterations.length > 20 ? (
        <div className="text-white/60">Large dataset - virtualization disabled</div>
      ) : (
        <div className="space-y-3">
          {iterations.map((iteration, index) => {
          const previousIteration = index > 0 ? iterations[index - 1] : undefined;
          const effectiveness = calculateEffectiveness(iteration, previousIteration);
          const isCurrentIteration = currentIteration !== undefined && iteration.iteration_number === currentIteration;
          const isUserTriggered = iteration.trigger === 'user_feedback';
          
          return (
            <div
              key={iteration.iteration_number}
              className={cn(
                'border rounded-lg p-4 transition-all duration-200 backdrop-blur-sm',
                isCurrentIteration 
                  ? 'border-cyan-400 bg-cyan-400/10 shadow-md' 
                  : 'border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10 cursor-pointer',
                onIterationSelect && 'hover:bg-white/10'
              )}
              onClick={() => onIterationSelect?.(iteration.iteration_number)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {/* Iteration Number */}
                  <div className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
                    isCurrentIteration 
                      ? 'bg-cyan-500 text-black' 
                      : 'bg-white/10 text-white'
                  )}>
                    {iteration.iteration_number}
                  </div>

                  {/* Trigger Badge */}
                  <div className={cn(
                    'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
                    isUserTriggered
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : 'bg-white/10 text-white/80 border border-white/20'
                  )}>
                    {isUserTriggered ? (
                      <>
                        <User className="w-3 h-3 mr-1" />
                        User Feedback
                      </>
                    ) : (
                      <>
                        <Bot className="w-3 h-3 mr-1" />
                        AI Evaluation
                      </>
                    )}
                  </div>

                  {/* Decision Badge */}
                  <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border border-white/20 bg-white/10 text-white/90">
                    {getDecisionIcon(iteration.decision)}
                    <span className="ml-1 capitalize">{iteration.decision}</span>
                  </div>

                  {/* Best Attempt Badge */}
                  {iteration.is_best_attempt && (
                    <div className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                      <Target className="w-3 h-3 mr-1" />
                      Best
                    </div>
                  )}
                </div>

                {/* Right-side badges */}
                <div className="flex items-center space-x-2">
                  {/* Feedback badge */}
                  <div className={cn(
                    'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border',
                    hasFeedback(iteration)
                      ? 'text-emerald-300 bg-emerald-500/20 border-emerald-500/30'
                      : 'text-white/50 bg-white/5 border-white/20'
                  )}>
                    Feedback
                  </div>

                  {/* Quality delta */}
                  {effectiveness.trend !== 'baseline' && (
                    <div className={cn(
                      'flex items-center text-xs',
                      effectiveness.trend === 'improving' ? 'text-green-400' : 
                      effectiveness.trend === 'declining' ? 'text-red-400' : 'text-white/60'
                    )}>
                      {effectiveness.trend === 'improving' && <TrendingUp className="w-3 h-3 mr-1" />}
                      {effectiveness.trend === 'declining' && <TrendingDown className="w-3 h-3 mr-1" />}
                      {effectiveness.improvement > 0 ? '+' : ''}{Math.round(effectiveness.improvement)}
                    </div>
                  )}
                  
                  {/* Overall score */}
                  <div className={cn(
                    'px-3 py-1 rounded-full text-sm font-semibold',
                    (iteration.scores.overall || 0) * 100 >= qualityThreshold
                      ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                      : (iteration.scores.overall || 0) * 100 >= qualityThreshold * 0.8
                      ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                      : 'bg-red-500/20 text-red-300 border border-red-500/30'
                  )}>
                    {Math.round((iteration.scores.overall || 0) * 100)}%
                  </div>
                </div>
              </div>

              {/* Persona and Timestamp */}
              <div className="flex items-center justify-between text-sm text-white/60 mb-3">
                <div className="flex items-center space-x-2">
                  <span>{getPersonaIcon(iteration.persona_used)}</span>
                  <span>{iteration.persona_used}</span>
                  <span>•</span>
                  <span>{formatTime(iteration.timestamp)}</span>
                </div>
                {iteration.processing_time_seconds && (
                  <div className="text-xs text-cyan-400">
                    {iteration.processing_time_seconds}s
                    {iteration.cost_usd && ` • $${iteration.cost_usd.toFixed(4)}`}
                  </div>
                )}
              </div>

              {/* Feedback Content */}
              {isUserTriggered && iteration.feedback_source.user_input && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-3 backdrop-blur-sm">
                  <div className="flex items-start space-x-2">
                    <MessageSquare className="w-4 h-4 text-purple-400 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-purple-300 mb-1">User Feedback</div>
                      <div className="text-sm text-white/80">
                        {iteration.feedback_source.user_input}
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {/* Improvements and Preserved Elements */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {iteration.improvements.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-white/80 mb-1">Improvements Made</div>
                    <div className="text-xs text-green-300 bg-green-500/10 border border-green-500/30 rounded p-2 backdrop-blur-sm">
                      {iteration.improvements.map((improvement, i) => (
                        <div key={i} className="flex items-start space-x-1">
                          <span>•</span>
                          <span>{improvement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {iteration.preserved_from_previous.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-white/80 mb-1">Elements Preserved</div>
                    <div className="text-xs text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded p-2 backdrop-blur-sm">
                      {iteration.preserved_from_previous.map((preserved, i) => (
                        <div key={i} className="flex items-start space-x-1">
                          <span>•</span>
                          <span>{preserved}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Section */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium text-white/80">Preview</div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const expanded = new Set(expandedIterations);
                      if (expanded.has(iteration.iteration_number)) {
                        expanded.delete(iteration.iteration_number);
                      } else {
                        expanded.add(iteration.iteration_number);
                      }
                      setExpandedIterations(expanded);
                    }}
                    className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                  >
                    {expandedIterations.has(iteration.iteration_number) ? 'Hide Full' : 'Show Full'}
                  </button>
                </div>
                
                {/* Preview Content */}
                <div className="bg-white/5 border border-white/20 rounded-lg p-3 mb-3 backdrop-blur-sm">
                  {iteration.assets && iteration.assets.length > 0 ? (
                    <div className="space-y-2">
                      {iteration.assets.slice(0, expandedIterations.has(iteration.iteration_number) ? undefined : 1).map((asset, assetIndex) => (
                        <div key={assetIndex} className="flex items-center space-x-3">
                          {asset.type === 'image' && (
                            <div className="w-16 h-12 bg-white/10 rounded border border-white/20 flex items-center justify-center text-xs text-white/60">
                              IMG
                            </div>
                          )}
                          {asset.type === 'video' && (
                            <div className="w-16 h-12 bg-white/10 rounded border border-white/20 flex items-center justify-center text-xs text-white/60">
                              VID
                            </div>
                          )}
                          {asset.type === 'text' && (
                            <div className="flex-1">
                              <div className="text-sm text-white/80 font-mono bg-black/20 border border-white/20 rounded p-2">
                                {expandedIterations.has(iteration.iteration_number) 
                                  ? asset.filename 
                                  : `${asset.filename.substring(0, 100)}${asset.filename.length > 100 ? '...' : ''}`
                                }
                              </div>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="text-xs text-white/70">{asset.filename}</div>
                            <div className="text-xs text-white/50 uppercase">{asset.type}</div>
                          </div>
                        </div>
                      ))}
                      {!expandedIterations.has(iteration.iteration_number) && iteration.assets.length > 1 && (
                        <div className="text-xs text-white/50">+ {iteration.assets.length - 1} more assets</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-white/50 italic">No preview content available</div>
                  )}
                </div>
              </div>

              {/* AI Evaluation */}
              {iteration.feedback_source.ai_evaluation && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs font-medium text-white/80 mb-2">AI Evaluation</div>
                  <div className="bg-white/5 border border-white/20 rounded-lg p-3 mb-3 backdrop-blur-sm">
                    <div className="text-sm text-white/80">
                      <strong className="text-cyan-400">Evaluation:</strong> {iteration.feedback_source.ai_evaluation.feedback}
                    </div>
                    {iteration.feedback_source.ai_evaluation.confidence && (
                      <div className="text-xs text-white/60 mt-2">
                        <strong className="text-cyan-400">Confidence:</strong> {Math.round(iteration.feedback_source.ai_evaluation.confidence * 100)}%
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quality Breakdown */}
              {iteration.scores && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs font-medium text-white/80 mb-2">Quality Breakdown</div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    {Object.entries(iteration.scores).filter(([key]) => key !== 'overall').map(([key, value]) => (
                      <div key={key} className="text-center">
                        <div className="text-white/60 capitalize">
                          {key.replace(/_/g, ' ').replace('anti cliche', 'anti-cliché')}
                        </div>
                        <div className={cn(
                          'font-semibold',
                          (typeof value === 'number' && value * 100 >= qualityThreshold) ? 'text-green-400' : 'text-white/80'
                        )}>
                          {typeof value === 'number' ? Math.round(value * 100) : 'N/A'}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* View Output */}
              {executionId && iteration.assets && iteration.assets.length > 0 && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={(e) => { e.stopPropagation(); openPreview(iteration); }}
                    className="px-3 py-1.5 text-xs rounded-md bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-colors"
                  >
                    View Output
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Summary Stats */}
      <div className="bg-white/5 border border-white/20 rounded-lg p-4 mt-4 backdrop-blur-sm">
        <div className="text-sm font-medium text-white mb-2">Session Summary</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-white/60">User Feedback</div>
            <div className="font-semibold text-white">
              {iterations.filter(i => i.trigger === 'user_feedback').length}
            </div>
          </div>
          <div>
            <div className="text-white/60">AI Evaluations</div>
            <div className="font-semibold text-white">
              {iterations.filter(i => i.trigger === 'ai_evaluation').length}
            </div>
          </div>
          <div>
            <div className="text-white/60">Quality Improvement</div>
            <div className="font-semibold text-green-600">
              +{Math.round(
                iterations.length > 1 
                  ? ((iterations[iterations.length - 1].scores.overall || 0) - (iterations[0].scores.overall || 0)) * 100
                  : 0
              )}%
            </div>
          </div>
          <div>
            <div className="text-white/60">Best Score</div>
            <div className="font-semibold text-white">
              {Math.round(Math.max(...iterations.map(i => (i.scores.overall || 0) * 100)))}%
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setPreview(null)}>
          <div className="bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-white/20 max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/20">
              <div className="text-sm font-medium text-white">{preview.title}</div>
              <button className="p-2 rounded hover:bg-white/10" onClick={() => setPreview(null)}><X className="w-4 h-4 text-white/70" /></button>
            </div>
            <div className="p-4">
              {preview.type === 'image' && (
                <img src={preview.url} alt="Iteration output" className="max-h-[70vh] mx-auto rounded border border-white/20" />
              )}
              {preview.type === 'video' && (
                <video src={preview.url} className="w-full rounded border border-white/20" controls />
              )}
              {preview.type === 'text' && (
                <div className="text-sm text-white/80 whitespace-pre-wrap">Open in a new tab: <a href={preview.url} className="text-cyan-400 underline" target="_blank" rel="noreferrer">{preview.url}</a></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IterationHistory;
